/**
 * Domain vocabulary — the mapping from a server value to how it looks and reads.
 *
 * Vehicle state, device health, alert severity and role all appear in the table,
 * the map, the stat tiles, the detail header and the filters. Defining
 * "degraded is amber and says Degraded" once means those five places can never
 * drift apart, and a new state is added in exactly one file.
 *
 * `tone` is a status token, never a brand or series colour — status colour is
 * reserved, and it always ships with a label so hue is never carrying the
 * meaning on its own.
 */

export const TONES = ['neutral', 'good', 'warning', 'serious', 'critical', 'brand', 'accent'];

/** Tailwind classes per tone, for pills and badges. Written once. */
export const TONE_CLASSES = {
  neutral: 'bg-neutral-soft text-ink-2 border-line',
  good: 'bg-good-soft text-good-ink border-good/30',
  warning: 'bg-warning-soft text-warning-ink border-warning/30',
  serious: 'bg-serious-soft text-serious-ink border-serious/30',
  critical: 'bg-critical-soft text-critical-ink border-critical/30',
  brand: 'bg-brand-soft text-brand-ink border-brand-border',
  accent: 'bg-accent-soft text-accent-ink border-accent-border',
};

/** Solid dot colour per tone, for map markers and legends. */
export const TONE_DOT = {
  neutral: 'bg-ink-3',
  good: 'bg-good',
  warning: 'bg-warning',
  serious: 'bg-serious',
  critical: 'bg-critical',
  brand: 'bg-brand',
  accent: 'bg-accent',
};

/** Raw hex per tone — for canvas, SVG and the map, which cannot use classes. */
export const TONE_HEX = {
  neutral: 'var(--ink-3)',
  good: 'var(--good)',
  warning: 'var(--warning)',
  serious: 'var(--serious)',
  critical: 'var(--critical)',
  brand: 'var(--brand)',
  accent: 'var(--accent)',
};

// ------------------------------------------------------------ vehicle state --

export const VEHICLE_STATES = {
  moving: { label: 'Moving', tone: 'good', icon: 'arrow', order: 1 },
  idle: { label: 'Idle', tone: 'warning', icon: 'pause', order: 2 },
  parked: { label: 'Parked', tone: 'neutral', icon: 'square', order: 3 },
  no_signal: { label: 'No signal', tone: 'critical', icon: 'slash', order: 4 },
  unassigned: { label: 'No device', tone: 'neutral', icon: 'dash', order: 5 },
};

export function vehicleState(vehicle) {
  if (!vehicle?.device_id) return { key: 'unassigned', ...VEHICLE_STATES.unassigned };
  const key = vehicle.online === false ? 'no_signal' : vehicle.vehicle_state || 'parked';
  return { key, ...(VEHICLE_STATES[key] || VEHICLE_STATES.parked) };
}

/** Fixed display order, so the legend and the status bar never reshuffle. */
export const VEHICLE_STATE_ORDER = Object.entries(VEHICLE_STATES)
  .sort((a, b) => a[1].order - b[1].order)
  .map(([key]) => key);

// ------------------------------------------------------------ device health --

export const HEALTH_BANDS = {
  healthy: { label: 'Healthy', tone: 'good', min: 85 },
  degraded: { label: 'Degraded', tone: 'warning', min: 60 },
  unhealthy: { label: 'Unhealthy', tone: 'critical', min: 0 },
  offline: { label: 'Offline', tone: 'neutral', min: null },
  unknown: { label: 'No data', tone: 'neutral', min: null },
};

/**
 * Offline overrides the score rather than scoring zero — an offline device
 * tells you nothing about its other signals, which is a different statement
 * from "this device is unhealthy".
 */
export function healthBand({ score, online }) {
  if (online === false) return { key: 'offline', ...HEALTH_BANDS.offline };
  if (score === null || score === undefined) return { key: 'unknown', ...HEALTH_BANDS.unknown };
  if (score >= 85) return { key: 'healthy', ...HEALTH_BANDS.healthy };
  if (score >= 60) return { key: 'degraded', ...HEALTH_BANDS.degraded };
  return { key: 'unhealthy', ...HEALTH_BANDS.unhealthy };
}

/** The health_flags jsonb from the server, turned into readable reasons. */
export const HEALTH_SIGNALS = {
  connectivity: 'Connectivity',
  fix: 'GNSS fix quality',
  signal: 'Cellular signal',
  continuity: 'Packet continuity',
  memory: 'Device memory',
  firmware: 'Firmware version',
  tamper: 'Tamper / tilt',
};

export function healthReasons(flags) {
  if (!flags || typeof flags !== 'object') return [];
  return Object.entries(flags)
    .filter(([key]) => HEALTH_SIGNALS[key])
    .map(([key, level]) => ({
      key,
      label: HEALTH_SIGNALS[key],
      level,
      tone: level === 'critical' ? 'critical' : 'warning',
    }))
    .sort((a, b) => (a.level === 'critical' ? -1 : 1));
}

// ---------------------------------------------------------------- alerts ----

export const SEVERITIES = {
  CRITICAL: { label: 'Critical', tone: 'critical', order: 1 },
  WARNING: { label: 'Warning', tone: 'warning', order: 2 },
  INFO: { label: 'Info', tone: 'brand', order: 3 },
};

export const ALERT_STATES = {
  OPEN: { label: 'Open', tone: 'critical' },
  ACKNOWLEDGED: { label: 'Acknowledged', tone: 'warning' },
  RESOLVED: { label: 'Resolved', tone: 'good' },
};

/** Alert kinds the ingest pipeline raises, given readable names. */
const ALERT_KINDS = {
  tamper_tilt: 'Tamper / tilt detected',
  device_unhealthy: 'Device health degraded',
  registration_mismatch: 'Registration mismatch',
};

export function alertTitle(alert) {
  if (!alert) return '';
  if (ALERT_KINDS[alert.kind]) return ALERT_KINDS[alert.kind];
  if (alert.kind?.startsWith('rule:')) return alert.message?.split(':')[0] || 'Rule triggered';
  return alert.kind || 'Alert';
}

// ----------------------------------------------------------------- roles ----

export const ROLES = {
  SUPER_ADMIN: { label: 'Platform admin', tone: 'accent', short: 'Platform' },
  ORG_ADMIN: { label: 'Organisation admin', tone: 'brand', short: 'Admin' },
  MANAGER: { label: 'Fleet manager', tone: 'good', short: 'Manager' },
  USER: { label: 'Viewer', tone: 'neutral', short: 'Viewer' },
};

export function roleMeta(role) {
  return ROLES[role] || { label: role, tone: 'neutral', short: role };
}
